// Native image lazy loading keeps the browser in charge of fetch scheduling.
// Skeletons add no image requests and never delay displaying a decoded image.
const pendingImages = new WeakSet();
let rotationBuilderPromise;

function prepareImage(img) {
  if (img.complete || pendingImages.has(img)) return;
  pendingImages.add(img);
  img.classList.add('media-skeleton');
  const finish = () => {
    img.classList.remove('media-skeleton');
    pendingImages.delete(img);
    img.removeEventListener('load', finish);
    img.removeEventListener('error', finish);
  };
  img.addEventListener('load', finish, { once: true });
  img.addEventListener('error', finish, { once: true });
  if (img.complete) finish();
}

function prepare(root) {
  if (!(root instanceof Element)) return;
  if (root.matches('img[loading="lazy"]')) prepareImage(root);
  root.querySelectorAll('img[loading="lazy"]').forEach(prepareImage);
  if (!rotationBuilderPromise && (root.matches('rotation-builder') || root.querySelector('rotation-builder'))) {
    rotationBuilderPromise = import('rotation-builder').catch((error) => {
      rotationBuilderPromise = null;
      console.error('Unable to load the rotation builder', error);
    });
  }
}

const videoVisibility = new IntersectionObserver((entries) => {
  for (const { target, isIntersecting } of entries) {
    if (!isIntersecting) target.pause();
  }
});

const galleryZoomFrames = new WeakMap();

function animateGalleryZoom(details, center = false) {
  const carousel = details.closest('.video-gallery__list');
  if (!carousel) return;
  window.cancelAnimationFrame(galleryZoomFrames.get(carousel));
  carousel.classList.add('is-zooming');
  const initialScroll = carousel.scrollLeft;
  const started = performance.now();
  const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 340;
  const step = () => {
    if (!details.isConnected) { carousel.classList.remove('is-zooming'); return; }
    const progress = duration ? Math.min((performance.now() - started) / duration, 1) : 1;
    if (center) {
      const tile = details.parentElement.getBoundingClientRect();
      const strip = carousel.getBoundingClientRect();
      const target = carousel.scrollLeft + tile.left - strip.left - (strip.width - tile.width) / 2;
      const eased = 1 - Math.pow(1 - progress, 3);
      carousel.scrollTo({ left: initialScroll + (target - initialScroll) * eased, behavior: 'instant' });
    }
    if (progress < 1) {
      galleryZoomFrames.set(carousel, window.requestAnimationFrame(step));
    } else {
      carousel.classList.remove('is-zooming');
      galleryZoomFrames.delete(carousel);
    }
  };
  galleryZoomFrames.set(carousel, window.requestAnimationFrame(step));
}

function closeGalleryVideo(details, restoreFocus = false) {
  const video = details.querySelector('video');
  video?.pause();
  backgroundVideos.delete(video);
  details.open = false;
  animateGalleryZoom(details);
  if (restoreFocus) details.querySelector('summary').focus({ preventScroll: true });
}

function openGalleryVideo(details) {
  document.querySelectorAll('details.video-gallery__item[open]').forEach((other) => {
    if (other !== details) closeGalleryVideo(other);
  });
  details.open = true;
  const template = details.querySelector('template');
  if (template) template.replaceWith(template.content.cloneNode(true));
  const video = details.querySelector('video');
  if (!video) return;
  video.playsInline = true;
  video.muted = false;
  // iOS requires play() in the original tap/keyboard click stack. The native
  // details "toggle" event is queued and loses that user gesture.
  video.play().catch(() => {
    // Keep native controls available for connection or playback failures.
  });

  // Follow the width transition instead of jumping the strip before it zooms.
  animateGalleryZoom(details, true);
  videoVisibility.observe(video);
  details.querySelector('.video-gallery__close')?.focus({ preventScroll: true });
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const close = event.target.closest('.video-gallery__close');
  const summary = event.target.closest('summary.video-gallery__thumb');
  if (!close && !summary) return;
  event.preventDefault();
  const details = (close || summary).closest('details.video-gallery__item');
  if (close || details.open) closeGalleryVideo(details, true);
  else openGalleryVideo(details);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
  const details = event.target.closest('details.video-gallery__item[open]');
  if (details) { event.preventDefault(); closeGalleryVideo(details, true); }
});

// Also pause when the theme editor or another script closes a tile.
document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (details instanceof Element && details.matches('details.video-gallery__item:not([open])')) {
    details.querySelector('video')?.pause();
    backgroundVideos.delete(details.querySelector('video'));
  }
}, true);

const backgroundVideos = new Set();
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    document.querySelectorAll('video').forEach((video) => {
      if (!video.paused) backgroundVideos.add(video);
      video.pause();
    });
  } else {
    backgroundVideos.forEach((video) => {
      const rect = video.getBoundingClientRect();
      if (video.isConnected && rect.width && rect.height && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth) {
        video.play().catch(() => {});
      }
    });
    backgroundVideos.clear();
  }
});

prepare(document.documentElement);
new MutationObserver((records) => {
  for (const record of records) {
    record.addedNodes.forEach(prepare);
    record.removedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches('video')) videoVisibility.unobserve(node);
      node.querySelectorAll('video').forEach((video) => videoVisibility.unobserve(video));
    });
  }
}).observe(document.body, { childList: true, subtree: true });
